// build: 2026-04-22-wave4a-v2
import { Suspense, lazy, useEffect, useState } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import FloatingBookButton from "./components/layout/FloatingBookButton";
import PromoBanner from "./components/layout/PromoBanner";
import ScrollToTop from "./components/ScrollToTop";
import { BookingWizardProvider } from "./contexts/BookingWizardContext";
import { BookingModalProvider } from "./contexts/BookingModalContext";
import BookingWizardModal from "./components/booking/BookingWizardModal";
const OvernightTonightPopupHost = lazy(() => import("./components/marketing/OvernightTonightPopupHost"));

// Lazy load Index page for consistent code splitting
const Index = lazy(() => import("./pages/Index"));

// Lazy load toasters to reduce initial JS bundle
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy load auth provider - only needed when user interacts with auth features
const AuthProvider = lazy(() => import("./hooks/useAuth").then(m => ({ default: m.AuthProvider })));

// Lazy load pages to reduce initial bundle size
const Book = lazy(() => import("./pages/Book"));
const Book5 = lazy(() => import("./pages/Book5"));

const Images5 = lazy(() => import("./pages/Images5"));
const BookNow = lazy(() => import("./pages/BookNow"));
// Legacy raw-Checkfront pages for protected sister properties are no longer
// routed for customers. Source files retained to avoid breaking unrelated
// imports. All /book-now/* + /bookfallback/* aliases for River Ranch, Island,
// Geronimo, and Blue River now redirect to each property's guarded booking
// page via <ExternalBookingRedirect />. Rio Cibolo aliases route to the
// already-guarded local flow (Book / Book5). Guadalupe is intentionally
// unchanged and still uses the legacy widget pages.
const BookSonsGuadalupe = lazy(() => import("./pages/BookSonsGuadalupe"));
const BookSonsGuadalupeOvernight = lazy(() => import("./pages/BookSonsGuadalupeOvernight"));
import ExternalBookingRedirect from "./components/booking/ExternalBookingRedirect";
const StayOvernight = lazy(() => import("./pages/StayOvernight"));
const DayRentals = lazy(() => import("./pages/DayRentals"));
const DayTripSanAntonio = lazy(() => import("./pages/DayTripSanAntonio"));
const DayTripAustin = lazy(() => import("./pages/DayTripAustin"));
const DayTripNewBraunfels = lazy(() => import("./pages/DayTripNewBraunfels"));
const DayTripHouston = lazy(() => import("./pages/DayTripHouston"));
const OvernightSanAntonio = lazy(() => import("./pages/OvernightSanAntonio"));
const OvernightAustin = lazy(() => import("./pages/OvernightAustin"));
const OvernightNewBraunfels = lazy(() => import("./pages/OvernightNewBraunfels"));
const OvernightHouston = lazy(() => import("./pages/OvernightHouston"));
const DayBooking = lazy(() => import("./pages/DayBooking"));
const OvernightBooking = lazy(() => import("./pages/OvernightBooking"));
const Specials = lazy(() => import("./pages/Specials"));
const PropertyMap = lazy(() => import("./pages/PropertyMap"));
const PlanYourVisit = lazy(() => import("./pages/PlanYourVisit"));
const OurStory = lazy(() => import("./pages/OurStory"));
const ReunionsGroups = lazy(() => import("./pages/ReunionsGroups"));
const OtherLocations = lazy(() => import("./pages/OtherLocations"));
const Blog = lazy(() => import("./pages/Blog"));

const BlogPostWhichSonsProperty = lazy(() => import("./pages/BlogPost-WhichSonsProperty"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminSubscribers = lazy(() => import("./pages/AdminSubscribers"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Waivers = lazy(() => import("./pages/Waivers"));
const SRCThankYou = lazy(() => import("./pages/SRCThankYou"));
const SharedWaivers = lazy(() => import("./pages/SharedWaivers"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const LeadForm = lazy(() => import("./pages/LeadForm"));
const SpringBreak = lazy(() => import("./pages/SpringBreak"));
const BlogPostSpringBreakRioCibolo = lazy(() => import("./pages/BlogPost-SpringBreakRioCibolo"));
const BlogPostGlampingNearSanAntonio = lazy(() => import("./pages/BlogPost-GlampingNearSanAntonio"));
const BlogPostCabinRentalsNearSanAntonio = lazy(() => import("./pages/BlogPost-CabinRentalsNearSanAntonio"));
const BlogPostKayakingNearSanAntonio = lazy(() => import("./pages/BlogPost-KayakingNearSanAntonio"));
const BlogPostFunThingsToDoNearSanAntonio = lazy(() => import("./pages/BlogPost-FunThingsToDoNearSanAntonio"));
const BlogPostTubingNearSanAntonio = lazy(() => import("./pages/BlogPost-TubingNearSanAntonio"));
const BlogPostWaterfrontCabinRentalsNearSanAntonio = lazy(() => import("./pages/BlogPost-WaterfrontCabinRentalsNearSanAntonio"));
const BlogPostFamilyGetawaysNearSanAntonio = lazy(() => import("./pages/BlogPost-FamilyGetawaysNearSanAntonio"));
const BlogPostFamilyReunionLocationsNearSanAntonio = lazy(() => import("./pages/BlogPost-FamilyReunionLocationsNearSanAntonio"));
const BlogPostBestGlampingSpotsNearNewBraunfels = lazy(() => import("./pages/BlogPost-BestGlampingSpotsNearNewBraunfels"));
const BlogPostGlampingNearAustin = lazy(() => import("./pages/BlogPost-GlampingNearAustin"));
const BlogPostGlampingNearHouston = lazy(() => import("./pages/BlogPost-GlampingNearHouston"));
const BlogPostThingsToDoNearNewBraunfels = lazy(() => import("./pages/BlogPost-ThingsToDoNearNewBraunfels"));
const BlogPostRomanticGetawaysNearSanAntonio = lazy(() => import("./pages/BlogPost-RomanticGetawaysNearSanAntonio"));
const BlogPostBacheloretteNearSanAntonio = lazy(() => import("./pages/BlogPost-BacheloretteNearSanAntonio"));
const BlogPostHillCountryCabinsCreekRiver = lazy(() => import("./pages/BlogPost-HillCountryCabinsCreekRiver"));
const BlogPostGlampingVsCamping = lazy(() => import("./pages/BlogPost-GlampingVsCamping"));
const BlogPostFallGetawaysNearSanAntonio = lazy(() => import("./pages/BlogPost-FallGetawaysNearSanAntonio"));
const BlogPostHalloweenSafariCabins = lazy(() => import("./pages/BlogPost-HalloweenSafariCabins"));
const BlogPostChristmasCabinRentalsNearSanAntonio = lazy(() => import("./pages/BlogPost-ChristmasCabinRentalsNearSanAntonio"));
const BlogPostMemorialDayJuly4thTexas = lazy(() => import("./pages/BlogPost-MemorialDayJuly4thTexas"));
const BlogPostCiboloCreekGuide = lazy(() => import("./pages/BlogPost-CiboloCreekGuide"));
const BlogPostCozyCabinsHillCountryGuide = lazy(() => import("./pages/BlogPost-CozyCabinsHillCountryGuide"));
const BlogPostCiboloCreekBestKeptSecret = lazy(() => import("./pages/BlogPost-CiboloCreekBestKeptSecret"));
const BlogPostMarionTexasGuide = lazy(() => import("./pages/BlogPost-MarionTexasGuide"));
const BlogPostThingsToDoInSeguinTx = lazy(() => import("./pages/BlogPost-ThingsToDoInSeguinTx"));
const BlogPostRioCiboloVsGeronimoVsRiverRanch = lazy(() => import("./pages/BlogPost-RioCiboloVsGeronimoVsRiverRanch"));
const BlogPostTexasGlampingGuide = lazy(() => import("./pages/BlogPost-TexasGlampingGuide"));
const CiboloCreekWaterConditions = lazy(() => import("./pages/CiboloCreekWaterConditions"));
const Compare = lazy(() => import("./pages/Compare"));
const CompareGeronimoCampFimfo = lazy(() => import("./pages/CompareGeronimoCampFimfo"));
const SonsVsTexasStateParks = lazy(() => import("./pages/SonsVsTexasStateParks"));
const SanAntonioAttractionsGuide = lazy(() => import("./pages/SanAntonioAttractionsGuide"));
const FamilyReunionPlanningGuide = lazy(() => import("./pages/FamilyReunionPlanningGuide"));
const FamilyOnlyPolicy = lazy(() => import("./pages/FamilyOnlyPolicy"));
const CiboloVsGuadalupe = lazy(() => import("./pages/CiboloVsGuadalupe"));
const FamilyKayakingGuide = lazy(() => import("./pages/FamilyKayakingGuide"));
const Easter = lazy(() => import("./pages/Easter"));
const RomanticGetaway = lazy(() => import("./pages/RomanticGetaway"));
const Pay = lazy(() => import("./pages/Pay"));
const Kayak = lazy(() => import("./pages/Kayak"));
const KayakHouston = lazy(() => import("./pages/KayakHouston"));
const KayakAustin = lazy(() => import("./pages/KayakAustin"));
const KayakNewBraunfels = lazy(() => import("./pages/KayakNewBraunfels"));
const KayakTexasHillCountry = lazy(() => import("./pages/KayakTexasHillCountry"));
const Double = lazy(() => import("./pages/Double"));
const Glamping = lazy(() => import("./pages/Glamping"));
const Camping = lazy(() => import("./pages/Camping"));
const CampingNearSanAntonio = lazy(() => import("./pages/CampingNearSanAntonio"));
const ThingsToDoNearSanAntonio = lazy(() => import("./pages/ThingsToDoNearSanAntonio"));
const GlampingSanAntonio = lazy(() => import("./pages/GlampingSanAntonio"));
const GlampingHouston = lazy(() => import("./pages/GlampingHouston"));
const GlampingAustin = lazy(() => import("./pages/GlampingAustin"));
const GlampingNewBraunfels = lazy(() => import("./pages/GlampingNewBraunfels"));
const GlampingTexasHillCountry = lazy(() => import("./pages/GlampingTexasHillCountry"));
const GlampingTexas = lazy(() => import("./pages/GlampingTexas"));
const FamilyCabins = lazy(() => import("./pages/FamilyCabins"));
const FamilyCabinsSanAntonio = lazy(() => import("./pages/FamilyCabinsSanAntonio"));
const FamilyCabinsHouston = lazy(() => import("./pages/FamilyCabinsHouston"));
const FamilyCabinsAustin = lazy(() => import("./pages/FamilyCabinsAustin"));
const FamilyCabinsNewBraunfels = lazy(() => import("./pages/FamilyCabinsNewBraunfels"));
const FamilyCabinsTexasHillCountry = lazy(() => import("./pages/FamilyCabinsTexasHillCountry"));
const WaterfrontCabins = lazy(() => import("./pages/WaterfrontCabins"));
const WaterfrontCabinsSanAntonio = lazy(() => import("./pages/WaterfrontCabinsSanAntonio"));
const WaterfrontCabinsAustin = lazy(() => import("./pages/WaterfrontCabinsAustin"));
const WaterfrontCabinsHouston = lazy(() => import("./pages/WaterfrontCabinsHouston"));
const WaterfrontCabinsNewBraunfels = lazy(() => import("./pages/WaterfrontCabinsNewBraunfels"));
const WaterfrontCabinsTexasHillCountry = lazy(() => import("./pages/WaterfrontCabinsTexasHillCountry"));
const Photos = lazy(() => import("./pages/Photos"));
const OGPhotos = lazy(() => import("./pages/OGPhotos"));
const SiteLinks = lazy(() => import("./pages/SiteLinks"));
const SiteLinks2 = lazy(() => import("./pages/SiteLinks2"));
const Activities = lazy(() => import("./pages/Activities"));
const Contact = lazy(() => import("./pages/Contact"));
const Reviews = lazy(() => import("./pages/Reviews"));
const CabinsNearSanAntonio = lazy(() => import("./pages/CabinsNearSanAntonio"));
const CabinsOnTheRiver = lazy(() => import("./pages/CabinsOnTheRiver"));
const CabinsOnTheRiverTexas = lazy(() => import("./pages/CabinsOnTheRiverTexas"));
const FamilyFunSanAntonio = lazy(() => import("./pages/FamilyFunSanAntonio"));
const FamilyFunHouston = lazy(() => import("./pages/FamilyFunHouston"));
const FamilyFunAustin = lazy(() => import("./pages/FamilyFunAustin"));
const FamilyFunNewBraunfels = lazy(() => import("./pages/FamilyFunNewBraunfels"));
const FamilyFunHillCountry = lazy(() => import("./pages/FamilyFunHillCountry"));
const CabinRentalsTexas = lazy(() => import("./pages/CabinRentalsTexas"));
const WeekendGetawaysNearSanAntonio = lazy(() => import("./pages/WeekendGetawaysNearSanAntonio"));
const GroupsRetreatsNearSanAntonio = lazy(() => import("./pages/GroupsRetreatsNearSanAntonio"));
const GlampingSanAntonioES = lazy(() => import("./pages/es/GlampingSanAntonioES"));
const BlogPostTexasGlampingCost2026 = lazy(() => import("./pages/BlogPost-TexasGlampingCost2026"));
const BlogPostBestWeekendTripsFromSanAntonio = lazy(() => import("./pages/BlogPost-BestWeekendTripsFromSanAntonio"));
const BlogPostBestFamilyCabinRentalsSanAntonio2026 = lazy(() => import("./pages/BlogPost-BestFamilyCabinRentalsSanAntonio2026"));
const BlogPostGlampingNearMeTexasHillCountry = lazy(() => import("./pages/BlogPost-GlampingNearMeTexasHillCountry"));
const BlogPostCampingSpotsNearMeSanAntonio = lazy(() => import("./pages/BlogPost-CampingSpotsNearMeSanAntonio"));
const BlogPostWeekendGetawaysNearSanAntonio = lazy(() => import("./pages/BlogPost-WeekendGetawaysNearSanAntonio"));

const NotFound = lazy(() => import("./pages/NotFound"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Map5Public = lazy(() => import("./features/map5/routes/Map5Public"));
const Map5Editor = lazy(() => import("./features/map5/routes/Map5Editor"));
const Map5EditorNumbered = lazy(() => import("./features/map5/routes/Map5EditorNumbered"));



const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Component to preload critical routes after initial render
const RoutePreloader = () => {
  useEffect(() => {
    // Kick off the interactive-map data + image prefetch immediately so the
    // map is ready the moment a user opens it from anywhere on the site.
    import("./features/map5/hooks/useMap5Data").then(({ prefetchMap5 }) => {
      prefetchMap5(queryClient).catch(() => {});
    });

    // Preload critical routes when browser is idle
    const preloadRoutes = () => {
      // Critical routes - highest priority
      import("./pages/BookNow");
      import("./pages/StayOvernight");
      import("./pages/DayRentals");
      import("./pages/Book");
    };

    const preloadSecondary = () => {
      // Secondary routes - lower priority
      import("./pages/OurStory");
      import("./pages/PlanYourVisit");
      import("./pages/Specials");
      import("./pages/PropertyMap");
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => preloadRoutes(), { timeout: 2000 });
      requestIdleCallback(() => preloadSecondary(), { timeout: 4000 });
    } else {
      setTimeout(preloadRoutes, 1500);
      setTimeout(preloadSecondary, 3500);
    }
  }, []);

  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Suspense fallback={null}>
          <AuthProvider>
            <BookingModalProvider>
              <Suspense fallback={null}>
                <Toaster />
                <Sonner />
              </Suspense>
              <BrowserRouter>
                <BookingWizardProvider>
                  <ScrollToTop />
                  <PromoBanner />
                  <Suspense fallback={null}>
                    <OvernightTonightPopupHost />
                  </Suspense>
                  <FloatingBookButton />
                  <BookingWizardModal />
                  <RoutePreloader />
                  <Suspense fallback={<PageLoader />}>

                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/book" element={<Book />} />
                      <Route path="/book5" element={<Book5 />} />
                      <Route path="/book-now" element={<Book5 />} />
                      <Route path="/control" element={<Navigate to="/" replace />} />

                      <Route path="/images5" element={<Images5 />} />
                      {/* Protected sister properties: hard external redirect to each property's guarded booking page. */}
                      <Route path="/book-now/geronimo" element={<ExternalBookingRedirect target="https://sonsgeronimo.com/book-now" />} />
                      <Route path="/book-now/river-ranch" element={<ExternalBookingRedirect target="https://sonsriverranch.com/book" />} />
                      <Route path="/book-now/river-ranch-overnight" element={<ExternalBookingRedirect target="https://sonsriverranch.com/book" />} />
                      <Route path="/book-now/blue-river-camp" element={<ExternalBookingRedirect target="https://bluerivercamp.com/book-now" />} />
                      <Route path="/book-now/blue-river-camp-overnight" element={<ExternalBookingRedirect target="https://bluerivercamp.com/book-now" />} />
                      {/* Local Rio Cibolo aliases: use the already-guarded local booking flow. */}
                      <Route path="/book-now/rio-cibolo" element={<Book />} />
                      <Route path="/book-now/rio-cibolo-overnight" element={<Book />} />
                      <Route path="/book-now/sons-island" element={<ExternalBookingRedirect target="https://sonsisland.com/book-now" />} />
                      <Route path="/book-now/sons-island-overnight" element={<ExternalBookingRedirect target="https://sonsisland.com/book-now" />} />
                      {/* Guadalupe intentionally unchanged — still uses legacy widget pages. */}
                      <Route path="/book-now/sons-guadalupe" element={<BookSonsGuadalupe />} />
                      <Route path="/book-now/sons-guadalupe-overnight" element={<BookSonsGuadalupeOvernight />} />
                      <Route path="/bookfallback" element={<BookNow />} />
                      <Route path="/bookfallback/geronimo" element={<ExternalBookingRedirect target="https://sonsgeronimo.com/book-now" />} />
                      <Route path="/bookfallback/river-ranch" element={<ExternalBookingRedirect target="https://sonsriverranch.com/book" />} />
                      <Route path="/bookfallback/river-ranch-overnight" element={<ExternalBookingRedirect target="https://sonsriverranch.com/book" />} />
                      <Route path="/bookfallback/blue-river-camp" element={<ExternalBookingRedirect target="https://bluerivercamp.com/book-now" />} />
                      <Route path="/bookfallback/blue-river-camp-overnight" element={<ExternalBookingRedirect target="https://bluerivercamp.com/book-now" />} />
                      <Route path="/bookfallback/rio-cibolo" element={<Navigate to="/book-now/rio-cibolo" replace />} />
                      <Route path="/bookfallback/rio-cibolo-overnight" element={<Navigate to="/book-now/rio-cibolo-overnight" replace />} />
                      <Route path="/bookfallback/sons-island" element={<ExternalBookingRedirect target="https://sonsisland.com/book-now" />} />
                      <Route path="/bookfallback/sons-island-overnight" element={<ExternalBookingRedirect target="https://sonsisland.com/book-now" />} />
                      <Route path="/bookfallback/sons-guadalupe" element={<BookSonsGuadalupe />} />
                      <Route path="/bookfallback/sons-guadalupe-overnight" element={<BookSonsGuadalupeOvernight />} />
                      <Route path="/stay-overnight" element={<StayOvernight />} />
                      <Route path="/day-rentals" element={<DayRentals />} />
                      <Route path="/day-trip-san-antonio" element={<DayTripSanAntonio />} />
                      <Route path="/day-trip-austin" element={<DayTripAustin />} />
                     <Route path="/day-trip-new-braunfels" element={<DayTripNewBraunfels />} />
                     <Route path="/day-trip-houston" element={<DayTripHouston />} />

                      <Route path="/overnight-san-antonio" element={<OvernightSanAntonio />} />
                      <Route path="/overnight-austin" element={<OvernightAustin />} />
                      <Route path="/overnight-new-braunfels" element={<OvernightNewBraunfels />} />
                      <Route path="/overnight-houston" element={<OvernightHouston />} />
                      <Route path="/day-booking" element={<Navigate to="/book-now?intent=day#sons-rio-cibolo" replace />} />
                      <Route path="/overnight-booking" element={<Navigate to="/book-now?intent=overnight#sons-rio-cibolo" replace />} />
                      <Route path="/specials" element={<Specials />} />
                      <Route path="/property-map" element={<PropertyMap />} />
                      <Route path="/plan-your-visit" element={<PlanYourVisit />} />
                      <Route path="/about" element={<OurStory />} />
                      <Route path="/reunions-groups" element={<ReunionsGroups />} />
                      <Route path="/locations" element={<OtherLocations />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/san-marcos-river-72-degrees" element={<Navigate to="/blog" replace />} />
                      <Route path="/blog/10-tips-family-river-day" element={<Navigate to="/blog" replace />} />
                      {/* glamping-vs-camping has a real post defined below */}
                      <Route path="/blog/which-sons-property-is-right" element={<BlogPostWhichSonsProperty />} />
                      <Route path="/blog/riverside-cabana-life" element={<Navigate to="/blog" replace />} />
                      <Route path="/blog/spring-break-family-memories" element={<BlogPostSpringBreakRioCibolo />} />
                      <Route path="/blog/spring-break-rio-cibolo" element={<Navigate to="/blog/spring-break-family-memories" replace />} />
                      <Route path="/family-cabins-waterfront-cabins" element={<Navigate to="/waterfront-cabins" replace />} />
                      <Route path="/blog/glamping-near-san-antonio" element={<BlogPostGlampingNearSanAntonio />} />
                      <Route path="/blog/cabin-rentals-near-san-antonio" element={<BlogPostCabinRentalsNearSanAntonio />} />
                      <Route path="/blog/kayaking-near-san-antonio" element={<BlogPostKayakingNearSanAntonio />} />
                      <Route path="/blog/fun-things-to-do-near-san-antonio" element={<BlogPostFunThingsToDoNearSanAntonio />} />
                      <Route path="/blog/tubing-near-san-antonio" element={<BlogPostTubingNearSanAntonio />} />
                      <Route path="/blog/waterfront-cabin-rentals-near-san-antonio" element={<BlogPostWaterfrontCabinRentalsNearSanAntonio />} />
                      <Route path="/blog/family-getaways-near-san-antonio" element={<BlogPostFamilyGetawaysNearSanAntonio />} />
                      <Route path="/blog/family-reunion-locations-near-san-antonio" element={<BlogPostFamilyReunionLocationsNearSanAntonio />} />
                      <Route path="/blog/best-glamping-spots-near-new-braunfels" element={<BlogPostBestGlampingSpotsNearNewBraunfels />} />
                      <Route path="/blog/glamping-near-austin" element={<BlogPostGlampingNearAustin />} />
                      <Route path="/blog/glamping-near-houston" element={<BlogPostGlampingNearHouston />} />
                      <Route path="/blog/things-to-do-near-new-braunfels" element={<BlogPostThingsToDoNearNewBraunfels />} />
                      <Route path="/blog/romantic-getaways-near-san-antonio" element={<BlogPostRomanticGetawaysNearSanAntonio />} />
                      <Route path="/blog/bachelorette-party-venues-near-san-antonio" element={<BlogPostBacheloretteNearSanAntonio />} />
                      <Route path="/blog/texas-hill-country-cabins-with-creek" element={<BlogPostHillCountryCabinsCreekRiver />} />
                      <Route path="/blog/glamping-vs-camping" element={<BlogPostGlampingVsCamping />} />
                      <Route path="/blog/fall-getaways-near-san-antonio" element={<BlogPostFallGetawaysNearSanAntonio />} />
                      <Route path="/blog/halloween-safari-cabins" element={<BlogPostHalloweenSafariCabins />} />
                      <Route path="/blog/christmas-cabin-rentals-near-san-antonio" element={<BlogPostChristmasCabinRentalsNearSanAntonio />} />
                      <Route path="/blog/memorial-day-july-4th-getaways-texas" element={<BlogPostMemorialDayJuly4thTexas />} />
                      <Route path="/blog/cibolo-creek-guide" element={<BlogPostCiboloCreekGuide />} />
                      <Route path="/blog/cozy-cabins-texas-hill-country-guide" element={<BlogPostCozyCabinsHillCountryGuide />} />
                      <Route path="/blog/cibolo-creek-hill-country-best-kept-secret" element={<BlogPostCiboloCreekBestKeptSecret />} />
                      <Route path="/blog/marion-texas-travel-guide" element={<BlogPostMarionTexasGuide />} />
                      <Route path="/blog/things-to-do-in-seguin-tx" element={<BlogPostThingsToDoInSeguinTx />} />
                      <Route path="/blog/rio-cibolo-vs-geronimo-vs-river-ranch" element={<BlogPostRioCiboloVsGeronimoVsRiverRanch />} />
                      <Route path="/blog/texas-glamping-guide" element={<BlogPostTexasGlampingGuide />} />
                      <Route path="/cibolo-creek-water-conditions" element={<CiboloCreekWaterConditions />} />
                      <Route path="/compare" element={<Compare />} />
                      <Route path="/compare/cibolo-vs-geronimo-vs-camp-fimfo" element={<CompareGeronimoCampFimfo />} />
                      <Route path="/sons-vs-texas-state-parks" element={<SonsVsTexasStateParks />} />
                      <Route path="/san-antonio-attractions-guide" element={<SanAntonioAttractionsGuide />} />
                      <Route path="/family-reunion-planning-guide" element={<FamilyReunionPlanningGuide />} />
                      <Route path="/family-only-policy" element={<FamilyOnlyPolicy />} />
                      <Route path="/cibolo-creek-vs-guadalupe-river" element={<CiboloVsGuadalupe />} />
                      <Route path="/activities/family-kayaking-guide" element={<FamilyKayakingGuide />} />
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/admin/subscribers" element={<AdminSubscribers />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/waivers" element={<Waivers />} />
                      <Route path="/srcthankyou" element={<SRCThankYou />} />
                      <Route path="/sharedwaivers" element={<SharedWaivers />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/inquiry" element={<LeadForm />} />
                       <Route path="/springbreak" element={<SpringBreak />} />
                       <Route path="/easter" element={<Easter />} />
                       <Route path="/romanticgetaway" element={<RomanticGetaway />} />
                       <Route path="/pay/:bookingCode" element={<Pay />} />
                       <Route path="/kayaksanantonio" element={<Kayak />} />
                       <Route path="/kayak-houston" element={<KayakHouston />} />
                       <Route path="/kayak-austin" element={<KayakAustin />} />
                       <Route path="/kayak-new-braunfels" element={<KayakNewBraunfels />} />
                       <Route path="/kayak-texas-hill-country" element={<KayakTexasHillCountry />} />
                       <Route path="/double" element={<Double />} />
                       <Route path="/glamping" element={<Glamping />} />
                       <Route path="/camping" element={<Camping />} />
                       <Route path="/camping-near-san-antonio" element={<CampingNearSanAntonio />} />
                       <Route path="/things-to-do-near-san-antonio" element={<ThingsToDoNearSanAntonio />} />
                       <Route path="/glamping-san-antonio" element={<GlampingSanAntonio />} />
                       <Route path="/glamping-houston" element={<GlampingHouston />} />
                       <Route path="/glamping-austin" element={<GlampingAustin />} />
                       <Route path="/glamping-new-braunfels" element={<GlampingNewBraunfels />} />
                       <Route path="/glamping-texas-hill-country" element={<GlampingTexasHillCountry />} />
                       <Route path="/glamping-texas" element={<GlampingTexas />} />
                       <Route path="/family-cabins" element={<FamilyCabins />} />
                       <Route path="/family-cabins-san-antonio" element={<FamilyCabinsSanAntonio />} />
                       <Route path="/family-cabins-houston" element={<FamilyCabinsHouston />} />
                       <Route path="/family-cabins-austin" element={<FamilyCabinsAustin />} />
                       <Route path="/family-cabins-new-braunfels" element={<FamilyCabinsNewBraunfels />} />
                       <Route path="/family-cabins-texas-hill-country" element={<FamilyCabinsTexasHillCountry />} />
                       <Route path="/waterfront-cabins" element={<WaterfrontCabins />} />
                       <Route path="/waterfront-cabins-san-antonio" element={<WaterfrontCabinsSanAntonio />} />
                       <Route path="/waterfront-cabins-austin" element={<WaterfrontCabinsAustin />} />
                       <Route path="/waterfront-cabins-houston" element={<WaterfrontCabinsHouston />} />
                       <Route path="/waterfront-cabins-new-braunfels" element={<WaterfrontCabinsNewBraunfels />} />
                       <Route path="/waterfront-cabins-texas-hill-country" element={<WaterfrontCabinsTexasHillCountry />} />
                       <Route path="/photos" element={<Photos />} />
                       <Route path="/ogphotos" element={<OGPhotos />} />
                       <Route path="/sitelinks" element={<SiteLinks />} />
                       <Route path="/sitelinks2" element={<SiteLinks2 />} />
                       <Route path="/activities" element={<Activities />} />
                       <Route path="/contact" element={<Contact />} />
                       <Route path="/reviews" element={<Reviews />} />
                       <Route path="/cabins-near-san-antonio" element={<CabinsNearSanAntonio />} />
                      <Route path="/cabins-on-the-river-near-san-antonio" element={<CabinsOnTheRiver />} />
                      <Route path="/cabins-on-the-river" element={<CabinsOnTheRiverTexas />} />
                       <Route path="/family-fun-san-antonio" element={<FamilyFunSanAntonio />} />
                       <Route path="/family-fun-houston" element={<FamilyFunHouston />} />
                       <Route path="/family-fun-austin" element={<FamilyFunAustin />} />
                       <Route path="/family-fun-new-braunfels" element={<FamilyFunNewBraunfels />} />
                       <Route path="/family-fun-hill-country" element={<FamilyFunHillCountry />} />
                       <Route path="/cabin-rentals-texas" element={<CabinRentalsTexas />} />
                       <Route path="/weekend-getaways-near-san-antonio" element={<WeekendGetawaysNearSanAntonio />} />
                       <Route path="/groups-retreats-near-san-antonio" element={<GroupsRetreatsNearSanAntonio />} />
                       <Route path="/es/glamping-san-antonio" element={<GlampingSanAntonioES />} />
                       <Route path="/blog/texas-glamping-cost-2026" element={<BlogPostTexasGlampingCost2026 />} />
                      <Route path="/blog/best-weekend-trips-from-san-antonio" element={<BlogPostBestWeekendTripsFromSanAntonio />} />
                      <Route path="/blog/best-family-cabin-rentals-san-antonio-2026" element={<BlogPostBestFamilyCabinRentalsSanAntonio2026 />} />
                      <Route path="/blog/glamping-near-me-texas-hill-country-guide" element={<BlogPostGlampingNearMeTexasHillCountry />} />
                     <Route path="/blog/camping-spots-near-me-san-antonio" element={<BlogPostCampingSpotsNearMeSanAntonio />} />
                     <Route path="/blog/weekend-getaways-near-san-antonio" element={<BlogPostWeekendGetawaysNearSanAntonio />} />

                      
                      <Route path="/san-antonio-cabin-rentals-1" element={<Navigate to="/" replace />} />
                       <Route path="/our-cabins" element={<Navigate to="/stay-overnight" replace />} />
                       <Route path="/cabins" element={<Navigate to="/stay-overnight" replace />} />
                       
                       <Route path="/tents" element={<Navigate to="/stay-overnight" replace />} />
                       <Route path="/safari-cabins" element={<Navigate to="/stay-overnight" replace />} />
                       <Route path="/cabanas" element={<Navigate to="/day-rentals" replace />} />
                       <Route path="/kayaking" element={<Navigate to="/kayaksanantonio" replace />} />
                       <Route path="/kayak" element={<Navigate to="/kayaksanantonio" replace />} />
                       <Route path="/reunions" element={<Navigate to="/reunions-groups" replace />} />
                       <Route path="/contact-us-1" element={<Navigate to="/plan-your-visit#contact" replace />} />
                       {/* Squarespace legacy paths (recovered from GSC impressions) */}
                       <Route path="/home" element={<Navigate to="/" replace />} />
                       <Route path="/faqs" element={<Navigate to="/plan-your-visit#faqs" replace />} />
                       <Route path="/donate" element={<Navigate to="/" replace />} />
                       <Route path="/our-story" element={<Navigate to="/about" replace />} />
                       <Route path="/gallery" element={<Navigate to="/photos" replace />} />
                        <Route path="/sitemap" element={<Sitemap />} />
                        <Route path="/map5" element={<Map5Public />} />
                        <Route path="/map5/editor" element={<Map5Editor />} />
                        <Route path="/map5/editor/numbered" element={<Map5EditorNumbered />} />
                        <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                </BookingWizardProvider>
              </BrowserRouter>
            </BookingModalProvider>
          </AuthProvider>

        </Suspense>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
